// src/hooks/usePushNotifications.js
// Manages browser push notification subscription for vendor dashboards.
// Requires a registered service worker (public/sw.js) and REACT_APP_VAPID_PUBLIC_KEY.

import { useState, useEffect, useCallback } from 'react';
import api from '../services/api';

// Convert base64 VAPID public key → Uint8Array for pushManager.subscribe()
function urlBase64ToUint8Array(base64) {
  const pad  = '='.repeat((4 - (base64.length % 4)) % 4);
  const b64  = (base64 + pad).replace(/-/g, '+').replace(/_/g, '/');
  const raw  = atob(b64);
  return Uint8Array.from([...raw].map(c => c.charCodeAt(0)));
}

const STORAGE_KEY = 'qs_push_subscribed';

export default function usePushNotifications() {
  const [supported,    setSupported]    = useState(false);
  const [permission,   setPermission]   = useState('default');
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [loading,      setLoading]      = useState(false);
  const [error,        setError]        = useState(null);

  const vapidKey = process.env.REACT_APP_VAPID_PUBLIC_KEY;

  useEffect(() => {
    const ok = 'serviceWorker' in navigator && 'PushManager' in window && !!vapidKey;
    setSupported(ok);
    setPermission(ok ? Notification.permission : 'unsupported');
    setIsSubscribed(ok && localStorage.getItem(STORAGE_KEY) === '1');
  }, [vapidKey]);

  const subscribe = useCallback(async () => {
    if (!supported) return;
    setLoading(true);
    setError(null);
    try {
      const perm = await Notification.requestPermission();
      setPermission(perm);
      if (perm !== 'granted') {
        setError('Notification permission denied.');
        return;
      }

      const reg = await navigator.serviceWorker.ready;
      const sub = await reg.pushManager.subscribe({
        userVisibleOnly:      true,
        applicationServerKey: urlBase64ToUint8Array(vapidKey),
      });

      await api.post('/vendors/me/push-subscription', sub.toJSON());
      localStorage.setItem(STORAGE_KEY, '1');
      setIsSubscribed(true);
    } catch (err) {
      setError(err.message || 'Subscription failed.');
    } finally {
      setLoading(false);
    }
  }, [supported, vapidKey]);

  const unsubscribe = useCallback(async () => {
    if (!supported) return;
    setLoading(true);
    setError(null);
    try {
      const reg = await navigator.serviceWorker.ready;
      const sub = await reg.pushManager.getSubscription();
      if (sub) {
        await api.delete('/vendors/me/push-subscription', { data: { endpoint: sub.endpoint } });
        await sub.unsubscribe();
      }
      localStorage.removeItem(STORAGE_KEY);
      setIsSubscribed(false);
    } catch (err) {
      setError(err.message || 'Unsubscribe failed.');
    } finally {
      setLoading(false);
    }
  }, [supported]);

  return { supported, permission, isSubscribed, loading, error, subscribe, unsubscribe };
}
